/**
 * Error reporting, Sentry-shaped: `captureException(error, context)`. Wave 1 reports to
 * nowhere (the console in dev). Wiring Sentry later = `setReporter(sentryReporter)` in
 * main.tsx; nothing else changes. Never put personal data (email, names, booking
 * reference) in `context`.
 */
import { ApiError, NetworkError } from '../api/errors';

export interface ErrorReporter {
  captureException(error: unknown, context?: Record<string, unknown>): void;
}

export const noopReporter: ErrorReporter = {
  captureException: (error, context) => {
    if (import.meta.env.DEV) console.error('[errors]', error, context ?? '');
  },
};

let current: ErrorReporter = noopReporter;

export function setReporter(r: ErrorReporter): void {
  current = r;
}

export function captureException(error: unknown, context?: Record<string, unknown>): void {
  try {
    current.captureException(error, context);
  } catch {
    /* reporting must never throw */
  }
}

/**
 * For failures a screen already handles (queries, mutations): a 4xx is an answer and the
 * network is the visitor's, so only a 5xx or something that isn't an API failure is a bug.
 */
export function isReportable(error: unknown): boolean {
  if (error instanceof NetworkError) return false;
  if (error instanceof DOMException && error.name === 'AbortError') return false;
  if (error instanceof ApiError) return error.status >= 500;
  return true;
}
